import React from 'react';

import styled, { css, DefaultTheme, keyframes } from 'styled-components';

import { BaseButton } from 'shared/button/style';
import { StyleType, IButton } from 'shared/button/type';

const UplusButton = ({
	size = 'default',
	_width,
	style_type = 'filled',
	text,
	_margin,
	_disabled = false,
	_onClick,
	_ref,
	is_loading,
}: IButton) => {
	if (size === 'large')
		return (
			<LargeUplusButton
				style_type={style_type}
				_width={_width}
				margin={_margin}
				disabled={_disabled}
				onClick={(e) => {
					if (is_loading || _onClick === undefined) return;
					_onClick(e);
				}}
				ref={_ref}
				is_loading={is_loading}
			>
				<span>{text}</span>
			</LargeUplusButton>
		);

	if (size === 'small')
		return (
			<SmallUplusButton
				style_type={style_type}
				_width={_width}
				margin={_margin}
				disabled={_disabled}
				onClick={(e) => {
					if (is_loading || _onClick === undefined) return;
					_onClick(e);
				}}
				ref={_ref}
				is_loading={is_loading}
			>
				<span>{text}</span>
			</SmallUplusButton>
		);

	return (
		<BaseUplusButton
			style_type={style_type}
			_width={_width}
			margin={_margin}
			disabled={_disabled}
			onClick={(e) => {
				if (is_loading || _onClick === undefined) return;
				_onClick(e);
			}}
			ref={_ref}
			tabIndex={0}
			is_loading={is_loading}
		>
			<span>{text}</span>
		</BaseUplusButton>
	);
};

const button_loading_spinner = keyframes`
	
	from {
	  transform: rotate(0turn);
	}
  
	to {
	  transform: rotate(1turn);
	}
  
`;

const color_mapper = {
	white: (theme: DefaultTheme) => theme.colors.gray.gray_white,
};
// Uplus
const BaseUplusButton = styled(BaseButton)<{ style_type: StyleType }>`
	// type - filled
	${(props) =>
		props.style_type === 'filled' &&
		css`
			--color: ${(props) => props.theme.colors.gray.gray_white};
			color: var(--color);
			background-color: ${({ theme }) => theme.colors.uplus.primary50_key};

			&:hover {
				background-color: ${(props) => props.theme.colors.uplus.primary60};
			}

			&:active {
				background-color: ${(props) => props.theme.colors.uplus.primary70};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				background-color: ${(props) => props.theme.colors.gray.gray15};
			}
		`}

	// type - tonal
    ${(props) =>
		props.style_type === 'tonal' &&
		css`
			--color: ${(props) => props.theme.colors.uplus.primary80_darken};
			color: var(--color);
			background-color: ${(props) => props.theme.colors.uplus.primary10_lighten};

			&:active {
				background-color: ${(props) => props.theme.colors.uplus.primary15};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				background-color: ${(props) => props.theme.colors.gray.gray15};
			}
		`}

    // type - outlined
    ${({ style_type, theme, color_type }) =>
		style_type === 'outlined' &&
		css`
			--color: ${() => {
				if (!color_type || color_type === 'default')
					return theme.colors.uplus.primary50_key;
				else return color_mapper[color_type](theme);
			}};
			color: var(--color);
			background-color: ${({ theme }) => theme.colors.gray.gray_white};
			border: 1px solid ${(props) => props.theme.colors.uplus.primary50_key};

			&:hover {
				background-color: ${(props) => props.theme.colors.uplus.primary10_lighten};
			}

			&:active {
				background-color: ${(props) => props.theme.colors.uplus.primary15};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				border-color: ${(props) => props.theme.colors.gray.gray15};
				background-color: ${({ theme }) => theme.colors.gray.gray_white};
			}
		`}

        // type - text
        ${(props) =>
		props.style_type === 'text' &&
		css`
			--color: ${(props) => props.theme.colors.uplus.primary50_key};
			color: var(--color);
			background-color: transparent;

			&:hover {
				background-color: ${(props) => props.theme.colors.uplus.primary10_lighten};
			}

			&:active {
				background-color: ${(props) => props.theme.colors.uplus.primary15};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				background-color: transparent;
			}
		`}

		${({ is_loading, theme }) =>
		is_loading &&
		css`
			& > span {
				visibility: hidden;
			}

			&::after {
				content: '';
				position: absolute;
				width: 16px;
				height: 16px;
				top: calc(50% - (8px + 2px));
				left: calc(50% - (8px + 2px));
				margin: auto;
				border: 2px solid transparent;
				border-top-color: var(--color);
				border-radius: 50%;
				animation: ${button_loading_spinner} 1s ease infinite;
			}
		`}
`;

const LargeUplusButton = styled(BaseUplusButton)`
	display: block;
	width: 100%;
	height: 56px;
	padding: 0;
`;

const SmallUplusButton = styled(BaseUplusButton)`
	height: 26.5px;
	padding: 0 8px;
	border-radius: 4px;
	&::after {
		width: 8px;
		height: 8px;
		top: calc(50% - (4px + 2px));
		left: calc(50% - (4px + 2px));
	}
`;

export default UplusButton;

// props
// - size: large, default, small
// - style_type: filled, tonal, outlined, text (피그마 참고)
// - text: button 텍스트
